/**
 * Hard rectangle-overlap removal — the guarantee behind "no two cards
 * ever overlap".
 *
 * Two stages:
 *   1. `separationPasses` — iterative min-penetration resolution. Every
 *      pass sweeps all pairs (sorted ids, deterministic), pushes each
 *      overlapping pair apart along the axis with the smaller penetration,
 *      splitting the move between free endpoints (a pinned endpoint is
 *      immobile; its partner takes the whole move). Cheap, quality-
 *      preserving (moves are minimal), converges for the huge majority of
 *      configurations but is not *guaranteed* to (pin squeezes, 3-body
 *      flapping).
 *   2. `resolveResidualOverlaps` — the guarantee. Any node still
 *      overlapping after the passes is re-placed sequentially (sorted
 *      order) onto the nearest free spot found by a growing spiral probe
 *      sized by its ACTUAL rect. Placement is conflict-checked against
 *      every node, so each placement is final — the loop terminates with
 *      zero overlaps by construction. Pinned-pinned overlaps are
 *      infeasible by definition and exempt everywhere.
 *
 * Both stages are pure position edits over the shared `pos` map — callers
 * wrap them in the solver generator and yield between passes.
 *
 * Guarantee scope: NON-DRAG solves. The drag budget skips separation (a
 * per-pointer-frame hard projection would fight the tuned sand feel), and
 * app-shell keeps a hand-placed board in drag mode until the user resets
 * the layout — so a hand-arranged board is user-owned territory where
 * near-overlaps are the user's call, held gently apart by repulsion only.
 */

import type { Rect } from "./stressLayout.js";

export interface SeparationArgs {
  /** Sorted node ids. */
  readonly ids: readonly string[];
  readonly pos: Map<string, { x: number; y: number }>;
  readonly size: ReadonlyMap<string, { w: number; h: number }>;
  readonly pinned: ReadonlyMap<string, Rect>;
  /** Required minimum rect-to-rect gap. */
  readonly gap: number;
  readonly maxPasses: number;
  /** Optional per-node extra clearance (crowd-scaled floor): the pair
   *  requirement becomes gap + padOf(a) + padOf(b). Dense-mesh members
   *  need a bigger GUARANTEED gap — spring ideals alone can't deliver it
   *  because the core compresses to this floor (user reference layout:
   *  core NN gap ~73px vs the uniform 29px floor). */
  readonly padOf?: (id: string) => number;
}

/** Axis gaps between two rects (negative = penetration on that axis). */
function axisGaps(
  a: { x: number; y: number },
  sa: { w: number; h: number },
  b: { x: number; y: number },
  sb: { w: number; h: number },
): { gapX: number; gapY: number } {
  return {
    gapX: Math.max(a.x, b.x) - Math.min(a.x + sa.w, b.x + sb.w),
    gapY: Math.max(a.y, b.y) - Math.min(a.y + sa.h, b.y + sb.h),
  };
}

/**
 * Iterative pairwise separation. Yields after each pass that moved
 * something (so the animation shows the un-squeeze), returns true when the
 * layout came out clean, false when maxPasses elapsed with residuals.
 */
export function* separationPasses(
  args: SeparationArgs,
): Generator<void, boolean, void> {
  const { ids, pos, size, pinned, gap, maxPasses, padOf } = args;
  const pairGap = padOf
    ? (a: string, b: string) => gap + padOf(a) + padOf(b)
    : (_a: string, _b: string) => gap;
  for (let pass = 0; pass < maxPasses; pass++) {
    let moved = false;
    for (let i = 0; i < ids.length; i++) {
      const idA = ids[i]!;
      const aPin = pinned.has(idA);
      for (let j = i + 1; j < ids.length; j++) {
        const idB = ids[j]!;
        const bPin = pinned.has(idB);
        if (aPin && bPin) continue;
        const a = pos.get(idA)!;
        const b = pos.get(idB)!;
        const sa = size.get(idA)!;
        const sb = size.get(idB)!;
        const need = pairGap(idA, idB);
        const { gapX, gapY } = axisGaps(a, sa, b, sb);
        if (gapX >= need || gapY >= need) continue;

        // Push along the axis with the smaller penetration.
        const penX = need - gapX;
        const penY = need - gapY;
        if (penX <= penY) {
          const dir = a.x + sa.w / 2 <= b.x + sb.w / 2 ? 1 : -1;
          if (aPin) b.x += dir * penX;
          else if (bPin) a.x -= dir * penX;
          else {
            a.x -= (dir * penX) / 2;
            b.x += (dir * penX) / 2;
          }
        } else {
          const dir = a.y + sa.h / 2 <= b.y + sb.h / 2 ? 1 : -1;
          if (aPin) b.y += dir * penY;
          else if (bPin) a.y -= dir * penY;
          else {
            a.y -= (dir * penY) / 2;
            b.y += (dir * penY) / 2;
          }
        }
        moved = true;
      }
    }
    if (!moved) return true;
    yield;
  }
  // One last clean check — the final pass may have resolved everything.
  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      const idA = ids[i]!;
      const idB = ids[j]!;
      if (pinned.has(idA) && pinned.has(idB)) continue;
      const { gapX, gapY } = axisGaps(
        pos.get(idA)!,
        size.get(idA)!,
        pos.get(idB)!,
        size.get(idB)!,
      );
      if (gapX < gap && gapY < gap) return false;
    }
  }
  return true;
}

export interface FrameSeparationArgs {
  /** Sorted node ids (one connected component's coarse ids). */
  readonly ids: readonly string[];
  readonly pos: Map<string, { x: number; y: number }>;
  readonly size: ReadonlyMap<string, { w: number; h: number }>;
  readonly pinned: ReadonlyMap<string, Rect>;
  /** Coarse id → its FINEST group (its HOME fragment). */
  readonly frameOf: ReadonlyMap<string, string>;
  /** Nested `group by`: group id → parent group id. Absent/empty ⇒ flat. */
  readonly parents?: ReadonlyMap<string, string>;
  /** Frame inflation: pad on every side, plus labelH extra on the label
   *  side. */
  readonly pad: number;
  readonly labelH: number;
  /** Which FLOW-space side carries the label strip — whichever side maps
   *  to SCREEN TOP under the flow direction: LR/RL → "top" (flow y-min),
   *  TB → "left" (flow x-min), BT → "right" (flow x-max). Default "top". */
  readonly labelSide?: FrameLabelSide;
  readonly maxPasses: number;
}

/** Flow-space side that carries the frame label strip (see labelSide). */
export type FrameLabelSide = "top" | "left" | "right";

/** Inflate a member bbox into a frame rect: pad all sides + labelH on the
 *  label side. */
function inflate(
  x0: number, y0: number, x1: number, y1: number,
  pad: number, labelH: number, side: FrameLabelSide,
): Rect {
  return {
    x: x0 - pad - (side === "left" ? labelH : 0),
    y: y0 - pad - (side === "top" ? labelH : 0),
    w: x1 - x0 + 2 * pad + (side === "left" || side === "right" ? labelH : 0),
    h: y1 - y0 + 2 * pad + (side === "top" ? labelH : 0),
  };
}

/** A frame fragment's inflated rect over its members' current rects. */
export function frameRect(
  members: readonly string[],
  pos: ReadonlyMap<string, { x: number; y: number }>,
  size: ReadonlyMap<string, { w: number; h: number }>,
  pad: number,
  labelH: number,
  labelSide: FrameLabelSide = "top",
): Rect {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const id of members) {
    const p = pos.get(id)!;
    const s = size.get(id)!;
    x0 = Math.min(x0, p.x);
    y0 = Math.min(y0, p.y);
    x1 = Math.max(x1, p.x + s.w);
    y1 = Math.max(y1, p.y + s.h);
  }
  return inflate(x0, y0, x1, y1, pad, labelH, labelSide);
}

/** One node of the fragment forest (nested `group by`). */
export interface FrameTreeNode {
  readonly id: string;
  readonly parentId?: string;
  readonly depth: number;
  readonly directMemberIds: readonly string[];
  readonly children: readonly FrameTreeNode[];
  /** Every descendant direct member (incl. own) — the rigid-translation set. */
  readonly subtreeMemberIds: readonly string[];
}

/**
 * Build the fragment forest for one CC from the node→finest-group map and
 * the group→parent map. Groups with no member and no descendant member in
 * this CC drop out. Deterministic: sorted ids at every level.
 */
export function buildFrameForest(
  ids: readonly string[],
  frameOf: ReadonlyMap<string, string>,
  parents: ReadonlyMap<string, string> | undefined,
): { roots: readonly FrameTreeNode[]; byId: ReadonlyMap<string, FrameTreeNode> } {
  const direct = new Map<string, string[]>();
  for (const id of ids) {
    const g = frameOf.get(id);
    if (g === undefined) continue;
    const arr = direct.get(g) ?? [];
    arr.push(id);
    direct.set(g, arr);
  }
  // Present groups + every ancestor of a present group.
  const present = new Set<string>();
  for (const g of direct.keys()) {
    let cur: string | undefined = g;
    while (cur !== undefined && !present.has(cur)) {
      present.add(cur);
      cur = parents?.get(cur);
    }
  }
  const childIds = new Map<string, string[]>();
  const rootIds: string[] = [];
  for (const g of [...present].sort()) {
    const par = parents?.get(g);
    if (par !== undefined && present.has(par)) {
      const arr = childIds.get(par) ?? [];
      arr.push(g);
      childIds.set(par, arr);
    } else {
      rootIds.push(g);
    }
  }
  const byId = new Map<string, FrameTreeNode>();
  const build = (g: string, parentId: string | undefined, depth: number): FrameTreeNode => {
    const children = (childIds.get(g) ?? []).map((c) => build(c, g, depth + 1));
    const own = [...(direct.get(g) ?? [])].sort();
    const subtree = [...own, ...children.flatMap((c) => c.subtreeMemberIds)];
    const node: FrameTreeNode = {
      id: g,
      ...(parentId !== undefined ? { parentId } : {}),
      depth,
      directMemberIds: own,
      children,
      subtreeMemberIds: subtree,
    };
    byId.set(g, node);
    return node;
  };
  const roots = rootIds.map((g) => build(g, undefined, 0));
  return { roots, byId };
}

/**
 * Frame-group integrity — the enforcer of the frame contract (frame-groups
 * plan; nested per the nested-group-by plan): fragment rects pairwise
 * disjoint per sibling scope, no non-member inside a rect it doesn't belong
 * to. Fragments move RIGIDLY (whole subtree translates) so solved interiors
 * survive; strangers and parent-pool members are lighter and relocate
 * individually. A fragment with ANY pinned descendant is frozen; pinned
 * strangers repel the fragment instead — a pin is the user's hand at every
 * depth. Pass schedule (pinned in the plan): (1) per parent scope, deepest
 * level first — sibling fragments pairwise, then pool-member eviction;
 * (2) top-level bodies pairwise; (3) homeless strangers vs the OUTERMOST
 * rects. Same generator shape as `separationPasses`.
 */
export function* frameSeparationPasses(
  args: FrameSeparationArgs,
): Generator<void, boolean, void> {
  const { ids, pos, size, pinned, frameOf, parents, pad, labelH, maxPasses } = args;
  const labelSide = args.labelSide ?? "top";
  const { roots, byId } = buildFrameForest(ids, frameOf, parents);
  if (byId.size === 0) return true;
  const frozen = new Set(
    [...byId.values()]
      .filter((n) => n.subtreeMemberIds.some((id) => pinned.has(id)))
      .map((n) => n.id),
  );
  const strangers = ids.filter((id) => !frameOf.has(id));
  const translate = (f: string, dx: number, dy: number): void => {
    for (const id of byId.get(f)!.subtreeMemberIds) {
      const p = pos.get(id)!;
      p.x += dx;
      p.y += dy;
    }
  };
  const overlap = (a: Rect, b: Rect): { penX: number; penY: number } => ({
    penX: Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x),
    penY: Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y),
  });
  // Sub-pixel penetrations are CLEAN: the node pass and this one nudge in
  // ~0.003px increments near their joint fixed point and would flap the
  // cycle budget away on a float residue no eye can see.
  const EPS = 0.5;
  const MARGIN = 8;
  // One rect rule: fragment rect = bbox(direct member rects ∪ child rects)
  // + pad + labelH per level — the exact recursion `placeGroupFrames` uses.
  const rectOf = (n: FrameTreeNode, memo: Map<string, Rect>): Rect => {
    const hit = memo.get(n.id);
    if (hit) return hit;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const id of n.directMemberIds) {
      const p = pos.get(id)!;
      const sz = size.get(id)!;
      x0 = Math.min(x0, p.x);
      y0 = Math.min(y0, p.y);
      x1 = Math.max(x1, p.x + sz.w);
      y1 = Math.max(y1, p.y + sz.h);
    }
    for (const c of n.children) {
      const r = rectOf(c, memo);
      x0 = Math.min(x0, r.x);
      y0 = Math.min(y0, r.y);
      x1 = Math.max(x1, r.x + r.w);
      y1 = Math.max(y1, r.y + r.h);
    }
    const r = inflate(x0, y0, x1, y1, pad, labelH, labelSide);
    memo.set(n.id, r);
    return r;
  };
  const refresh = (n: FrameTreeNode, memo: Map<string, Rect>): void => {
    // Invalidate this fragment + every ancestor, recompute lazily.
    let cur: FrameTreeNode | undefined = n;
    while (cur !== undefined) {
      memo.delete(cur.id);
      cur = cur.parentId !== undefined ? byId.get(cur.parentId) : undefined;
    }
  };
  const ancestorsOf = (fragId: string): Set<string> => {
    const out = new Set<string>();
    let cur: FrameTreeNode | undefined = byId.get(fragId);
    while (cur !== undefined) {
      out.add(cur.id);
      cur = cur.parentId !== undefined ? byId.get(cur.parentId) : undefined;
    }
    return out;
  };
  /** Landing clear of every fragment rect except the allowed set. */
  const insideForbidden = (
    x: number,
    y: number,
    s: { w: number; h: number },
    allowed: ReadonlySet<string>,
    memo: Map<string, Rect>,
  ): boolean => {
    for (const n of byId.values()) {
      if (allowed.has(n.id)) continue;
      const r = rectOf(n, memo);
      const { penX, penY } = overlap(r, { x, y, w: s.w, h: s.h });
      if (penX > EPS && penY > EPS) return true;
    }
    return false;
  };
  const spiral = (
    px: number,
    py: number,
    s: { w: number; h: number },
    ok: (x: number, y: number) => boolean,
    maxRings: number,
  ): { dx: number; dy: number } | undefined => {
    const STEP = 40;
    for (let radius = STEP; radius <= STEP * maxRings; radius += STEP) {
      const probes = Math.max(8, Math.round((2 * Math.PI * radius) / STEP));
      for (let k = 0; k < probes; k++) {
        const a = (2 * Math.PI * k) / probes;
        const dx = Math.round(Math.cos(a) * radius);
        const dy = Math.round(Math.sin(a) * radius);
        if (ok(px + dx, py + dy)) return { dx, dy };
      }
    }
    return undefined;
  };
  /** Four exits from rect r, cheapest first. */
  const exitsFrom = (r: Rect, p: { x: number; y: number }, s: { w: number; h: number }) =>
    [
      { dx: r.x - (p.x + s.w) - MARGIN, dy: 0 },
      { dx: r.x + r.w - p.x + MARGIN, dy: 0 },
      { dx: 0, dy: r.y - (p.y + s.h) - MARGIN },
      { dx: 0, dy: r.y + r.h - p.y + MARGIN },
    ].sort((a, b) => Math.abs(a.dx + a.dy) - Math.abs(b.dx + b.dy));

  const maxDepth = Math.max(0, ...[...byId.values()].map((n) => n.depth));

  for (let pass = 0; pass < maxPasses; pass++) {
    let moved = false;
    const memo = new Map<string, Rect>();

    // Pairwise rigid separation of one sibling list (shared by every scope).
    const separateSiblings = (siblings: readonly FrameTreeNode[]): void => {
      for (let i = 0; i < siblings.length; i++) {
        for (let j = i + 1; j < siblings.length; j++) {
          const na = siblings[i]!;
          const nb = siblings[j]!;
          const fa = na.id;
          const fb = nb.id;
          if (frozen.has(fa) && frozen.has(fb)) continue;
          const ra = rectOf(na, memo);
          const rb = rectOf(nb, memo);
          const { penX, penY } = overlap(ra, rb);
          if (penX <= EPS || penY <= EPS) continue;
          if (penX <= penY) {
            const dir = ra.x + ra.w / 2 <= rb.x + rb.w / 2 ? 1 : -1;
            if (frozen.has(fa)) translate(fb, dir * penX, 0);
            else if (frozen.has(fb)) translate(fa, -dir * penX, 0);
            else {
              translate(fa, (-dir * penX) / 2, 0);
              translate(fb, (dir * penX) / 2, 0);
            }
          } else {
            const dir = ra.y + ra.h / 2 <= rb.y + rb.h / 2 ? 1 : -1;
            if (frozen.has(fa)) translate(fb, 0, dir * penY);
            else if (frozen.has(fb)) translate(fa, 0, -dir * penY);
            else {
              translate(fa, 0, (-dir * penY) / 2);
              translate(fb, 0, (dir * penY) / 2);
            }
          }
          refresh(na, memo);
          refresh(nb, memo);
          moved = true;
        }
      }
    };

    // (1) Per parent scope, deepest level first: siblings, then the
    //     parent's pool members via constrained eviction.
    for (let d = maxDepth; d >= 1; d--) {
      const parentsAtLevel = [...byId.values()]
        .filter((n) => n.depth === d - 1 && n.children.length > 0)
        .sort((a, b) => (a.id < b.id ? -1 : 1));
      for (const P of parentsAtLevel) {
        separateSiblings(P.children);
        // Pool members of P vs each child rect: the member moves
        // (constrained eviction), except a pinned member repels the child.
        const homeAllowed = ancestorsOf(P.id);
        for (const m of P.directMemberIds) {
          const p = pos.get(m)!;
          const s = size.get(m)!;
          for (const child of P.children) {
            const r = rectOf(child, memo);
            const { penX, penY } = overlap(r, { x: p.x, y: p.y, w: s.w, h: s.h });
            if (penX <= EPS || penY <= EPS) continue;
            if (pinned.has(m)) {
              if (frozen.has(child.id)) continue;
              const nodeCx = p.x + s.w / 2;
              const nodeCy = p.y + s.h / 2;
              const dx = nodeCx <= r.x + r.w / 2 ? -penX : penX;
              const dy = nodeCy <= r.y + r.h / 2 ? -penY : penY;
              if (penX <= penY) translate(child.id, -dx, 0);
              else translate(child.id, 0, -dy);
              refresh(child, memo);
              moved = true;
              continue;
            }
            const pRect = rectOf(P, memo);
            const insideHome = (x: number, y: number): boolean => {
              const pen = overlap(pRect, { x, y, w: s.w, h: s.h });
              return pen.penX >= s.w - EPS && pen.penY >= s.h - EPS;
            };
            // Stage A: cheapest exit landing inside home, outside siblings.
            const exits = exitsFrom(r, p, s);
            let pick = exits.find(
              (e) =>
                insideHome(p.x + e.dx, p.y + e.dy) &&
                !insideForbidden(p.x + e.dx, p.y + e.dy, s, homeAllowed, memo),
            );
            // Stage A2: in-home spiral.
            pick ??= spiral(p.x, p.y, s, (x, y) =>
              insideHome(x, y) && !insideForbidden(x, y, s, homeAllowed, memo), 40);
            // Stage B (relax ONE clause): may leave home, but still clear of
            // every NON-ANCESTOR rect — landing inside an uncle would mint a
            // fresh violation and churn.
            pick ??= exits.find(
              (e) => !insideForbidden(p.x + e.dx, p.y + e.dy, s, homeAllowed, memo),
            );
            pick ??= spiral(p.x, p.y, s, (x, y) =>
              !insideForbidden(x, y, s, homeAllowed, memo), 40);
            // Stage C: unconditional tightest edge — final fallback; churn
            // bounded by the cycle cap + terminal residue acceptance.
            pick ??= exits[0]!;
            p.x += pick.dx;
            p.y += pick.dy;
            refresh(P, memo);
            moved = true;
          }
        }
      }
    }

    // (2) Top level: subtree bodies pairwise (the classic frame–frame pass;
    //     depth-1 forests make this the whole story).
    separateSiblings(roots);

    // (3) Homeless strangers vs the OUTERMOST rects: cheapest clear exit,
    //     spiral fallback for tiled pockets; pinned strangers repel the body.
    const NO_ALLOWANCE = new Set<string>();
    for (const id of strangers) {
      const p = pos.get(id)!;
      const s = size.get(id)!;
      for (const root of roots) {
        const r = rectOf(root, memo);
        const { penX, penY } = overlap(r, { x: p.x, y: p.y, w: s.w, h: s.h });
        if (penX <= EPS || penY <= EPS) continue;
        if (pinned.has(id)) {
          if (frozen.has(root.id)) continue;
          const nodeCx = p.x + s.w / 2;
          const nodeCy = p.y + s.h / 2;
          const dx = nodeCx <= r.x + r.w / 2 ? -penX : penX;
          const dy = nodeCy <= r.y + r.h / 2 ? -penY : penY;
          if (penX <= penY) translate(root.id, -dx, 0);
          else translate(root.id, 0, -dy);
          refresh(root, memo);
          moved = true;
          continue;
        }
        const exits = exitsFrom(r, p, s);
        let pick = exits.find(
          (e) => !insideForbidden(p.x + e.dx, p.y + e.dy, s, NO_ALLOWANCE, memo),
        );
        pick ??= spiral(p.x, p.y, s, (x, y) => !insideForbidden(x, y, s, NO_ALLOWANCE, memo), 40);
        if (pick) {
          p.x += pick.dx;
          p.y += pick.dy;
          moved = true;
        }
      }
    }
    if (!moved) return true;
    yield;
  }
  return false;
}

/**
 * Terminal frame guarantee: any FREE node still violating the frame
 * contract after the interleaved frame/node cycles is spiral-relocated so
 * that neither pass has a reason to move it again — a homeless stranger
 * lands clear of every OUTERMOST rect; a parent-pool member lands inside
 * its home parent and clear of every other rect (relaxing to
 * beyond-the-home-edge-but-clear-of-non-ancestors when the parent is
 * full — the bbox follows). All landings are node-collision-free
 * (pair-gap aware). The oscillation this kills: a node squeezed in a
 * corridor narrower than its clearance needs orbits the two passes
 * forever. Returns the ids it moved.
 */
export function resolveFrameStrangerResiduals(
  args: FrameSeparationArgs & {
    readonly gap: number;
    readonly padOf?: (id: string) => number;
  },
): string[] {
  const { ids, pos, size, pinned, frameOf, parents, pad, labelH, gap, padOf } = args;
  const labelSide = args.labelSide ?? "top";
  const { roots, byId } = buildFrameForest(ids, frameOf, parents);
  if (byId.size === 0) return [];
  const EPS = 0.5;
  const memo = new Map<string, Rect>();
  const rectOf = (n: FrameTreeNode): Rect => {
    const hit = memo.get(n.id);
    if (hit) return hit;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const id of n.directMemberIds) {
      const pp = pos.get(id)!;
      const sz = size.get(id)!;
      x0 = Math.min(x0, pp.x);
      y0 = Math.min(y0, pp.y);
      x1 = Math.max(x1, pp.x + sz.w);
      y1 = Math.max(y1, pp.y + sz.h);
    }
    for (const c of n.children) {
      const r = rectOf(c);
      x0 = Math.min(x0, r.x);
      y0 = Math.min(y0, r.y);
      x1 = Math.max(x1, r.x + r.w);
      y1 = Math.max(y1, r.y + r.h);
    }
    const r = inflate(x0, y0, x1, y1, pad, labelH, labelSide);
    memo.set(n.id, r);
    return r;
  };
  const pen = (r: Rect, x: number, y: number, s: { w: number; h: number }) => ({
    penX: Math.min(r.x + r.w, x + s.w) - Math.max(r.x, x),
    penY: Math.min(r.y + r.h, y + s.h) - Math.max(r.y, y),
  });
  const insideExcept = (
    x: number,
    y: number,
    s: { w: number; h: number },
    allowed: ReadonlySet<string>,
  ): boolean => {
    for (const n of byId.values()) {
      if (allowed.has(n.id)) continue;
      const { penX, penY } = pen(rectOf(n), x, y, s);
      if (penX > EPS && penY > EPS) return true;
    }
    return false;
  };
  const containedIn = (r: Rect, x: number, y: number, s: { w: number; h: number }): boolean => {
    const q = pen(r, x, y, s);
    return q.penX >= s.w - EPS && q.penY >= s.h - EPS;
  };
  const pairGap = (a: string, b: string): number =>
    gap + (padOf?.(a) ?? 0) + (padOf?.(b) ?? 0);
  const collides = (id: string, x: number, y: number): boolean => {
    const s = size.get(id)!;
    for (const other of ids) {
      if (other === id) continue;
      const o = pos.get(other)!;
      const so = size.get(other)!;
      const need = pairGap(id, other);
      const gapX = Math.max(x, o.x) - Math.min(x + s.w, o.x + so.w);
      const gapY = Math.max(y, o.y) - Math.min(y + s.h, o.y + so.h);
      if (gapX < need && gapY < need) return true;
    }
    return false;
  };
  const spiralTo = (
    id: string,
    ok: (x: number, y: number) => boolean,
  ): boolean => {
    const p = pos.get(id)!;
    const STEP = 40;
    for (let radius = STEP; radius <= STEP * 60; radius += STEP) {
      const probes = Math.max(8, Math.round((2 * Math.PI * radius) / STEP));
      for (let k = 0; k < probes; k++) {
        const a = (2 * Math.PI * k) / probes;
        const x = p.x + Math.round(Math.cos(a) * radius);
        const y = p.y + Math.round(Math.sin(a) * radius);
        if (ok(x, y) && !collides(id, x, y)) {
          p.x = x;
          p.y = y;
          return true;
        }
      }
    }
    return false;
  };
  const ancestorsOf = (fragId: string): Set<string> => {
    const out = new Set<string>();
    let cur: FrameTreeNode | undefined = byId.get(fragId);
    while (cur !== undefined) {
      out.add(cur.id);
      cur = cur.parentId !== undefined ? byId.get(cur.parentId) : undefined;
    }
    return out;
  };
  const movedIds: string[] = [];
  for (const id of ids) {
    if (pinned.has(id)) continue;
    const p = pos.get(id)!;
    const s = size.get(id)!;
    const home = frameOf.get(id);
    if (home === undefined) {
      // Homeless: must be clear of every rect (roots suffice — children are
      // inside their roots — but checking all is equivalent and simple).
      if (!insideExcept(p.x, p.y, s, new Set())) continue;
      if (spiralTo(id, (x, y) => !insideExcept(x, y, s, new Set()))) movedIds.push(id);
      continue;
    }
    // Pool/home member: violating when inside any non-ancestor rect OR
    // outside its home rect.
    const allowed = ancestorsOf(home);
    const homeNode = byId.get(home);
    if (!homeNode) continue; // malformed parents stranded the home outside the forest
    const violating =
      insideExcept(p.x, p.y, s, allowed) || !containedIn(rectOf(homeNode), p.x, p.y, s);
    if (!violating) continue;
    const homeRect = rectOf(homeNode);
    const relocated =
      spiralTo(id, (x, y) => containedIn(homeRect, x, y, s) && !insideExcept(x, y, s, allowed)) ||
      // Relaxed: may leave home, still clear of non-ancestors — the home
      // bbox follows the landing on the next placement.
      spiralTo(id, (x, y) => !insideExcept(x, y, s, allowed));
    if (relocated) movedIds.push(id);
  }
  return movedIds;
}

/**
 * Terminal guarantee: sequentially re-place every free node still involved
 * in an overlap onto the nearest free spot (spiral probe, actual rect
 * size). Deterministic; terminates with zero non-exempt overlaps.
 * Returns the ids it moved (empty in the common already-clean case).
 */
export function resolveResidualOverlaps(args: SeparationArgs): string[] {
  const { ids, pos, size, pinned, gap } = args;

  const overlappingFreeNodes = (): string[] => {
    const bad = new Set<string>();
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        const idA = ids[i]!;
        const idB = ids[j]!;
        const aPin = pinned.has(idA);
        const bPin = pinned.has(idB);
        if (aPin && bPin) continue;
        const { gapX, gapY } = axisGaps(
          pos.get(idA)!,
          size.get(idA)!,
          pos.get(idB)!,
          size.get(idB)!,
        );
        if (gapX < gap && gapY < gap) {
          // Prefer moving the free node; both free → both candidates
          // (dedup via the set; placement order is sorted anyway).
          if (!aPin) bad.add(idA);
          if (!bPin) bad.add(idB);
        }
      }
    }
    return [...bad].sort();
  };

  const isFreeAt = (id: string, x: number, y: number): boolean => {
    const s = size.get(id)!;
    for (const other of ids) {
      if (other === id) continue;
      const o = pos.get(other)!;
      const so = size.get(other)!;
      const gapX = Math.max(x, o.x) - Math.min(x + s.w, o.x + so.w);
      const gapY = Math.max(y, o.y) - Math.min(y + s.h, o.y + so.h);
      if (gapX < gap && gapY < gap) return false;
    }
    return true;
  };

  const moved: string[] = [];
  for (const id of overlappingFreeNodes()) {
    const p = pos.get(id)!;
    // Non-finite coordinates (should be impossible — the solver unsticks
    // coincident pairs before NaN can arise) would make every isFreeAt
    // comparison read "free" and the spiral meaningless; skip defensively.
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    if (isFreeAt(id, p.x, p.y)) continue; // earlier placement freed it
    // Spiral probe: radius grows until placement succeeds. The layout
    // bbox is finite, so a sufficiently large radius always lands in
    // empty space — the loop terminates by construction. 16 angles,
    // starting from 0 (east), deterministic order.
    let placed = false;
    for (let radius = gap + 40; !placed; radius += 60) {
      for (let a = 0; a < 16; a++) {
        const ang = (a * Math.PI) / 8;
        const x = p.x + Math.cos(ang) * radius;
        const y = p.y + Math.sin(ang) * radius;
        if (isFreeAt(id, x, y)) {
          p.x = x;
          p.y = y;
          placed = true;
          moved.push(id);
          break;
        }
      }
      // Safety valve — bbox of any realistic board is far below this.
      if (radius > 1e6) break;
    }
  }
  return moved;
}
